import {
  Button,
  Flex,
  Label,
  ModalManagerContext,
  Text,
  Image,
  Grid,
  TextArea,
  Icon,
  Banner,
  Link,
  Tooltip,
  useElement,
} from '@pega/cosmos-react-core';
import { ModalMethods } from '@pega/cosmos-react-core/lib/components/Modal/Modal.types';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { updateBannerInfo, updateLoaderDetails } from '../../../redux/actions';
import { ConfirmationAlert } from '../../Utilities/CustomModal';
import SectionRenderTemplate from '../../Utilities/SectionRenderTemplate';
import * as Logos from '../../../constants/images';
import { appState, confirmationAlertProps } from '../../../interfaces/common';
import useLocalTranslation from '../../../hooks/useLocalTranslation';
import { deleteAccount, getAccountDetails, updateApiKey } from './LanguageServiceApis';
import { getMaskedKey } from '../../../utils/helpers';
import { tokenMaskerStyle } from '../../Utilities/custom.styled';
import { useForm } from 'react-hook-form';
import { GoogleApiKeyGenLink } from '../../../constants/common';
import { validateApiKey } from '../../SocialMedia/Chat/Tabs/LanguageTab/ApiKeyValidation';
import { usePlatformVersionSelector } from '../../../redux/selectors';

type AccountDetails = {
  accountName: string;
  apiKey: string;
  isApiKeyValid: boolean;
  service: string;
  isLanguageServiceInUse: boolean;
};

const LanguageService: React.FC<any> = () => {
  const { id: connectionId }: any = useParams();
  const sessionId = useSelector((state: appState) => state.sessionId);
  const initialState: AccountDetails = {
    accountName: '',
    apiKey: '',
    isApiKeyValid: true,
    service: '',
    isLanguageServiceInUse: false,
  };
  const [accountDetails, setaccountDetails] = useState(initialState);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  const { create } = useContext(ModalManagerContext);
  const [toggle, setToggle] = useState(false);
  const [editFields, setEditFields] = useState(false);
  const { apiKey, accountName, isApiKeyValid, isLanguageServiceInUse } = accountDetails;
  const { register, handleSubmit, errors, formState, reset } = useForm({
    mode: 'all',
  });
  const [showTooltipEl, setShowTooltipEl] = useElement();
  const location = useLocation();
  const { majorVersion, minorVersion } = usePlatformVersionSelector();
  const beyondEightDotSeven =
    majorVersion && (majorVersion > 8 || (majorVersion === 8 && minorVersion && minorVersion >= 7));
  useEffect(() => {
    const updatedData = {
      apiKey: apiKey,
    };
    reset(updatedData);
  }, [apiKey, reset]);

  const modalMethods = useRef<ModalMethods<Partial<confirmationAlertProps>>>();
  const deleteConfirmation = () => {
    modalMethods.current = create(
      ConfirmationAlert,
      { pageName: accountName, deleteChannel: deleteChannel },
      { alert: true },
    );
  };

  const fetchAccountDetails = useCallback(async () => {
    dispatch(updateLoaderDetails(true));
    let response = await getAccountDetails(sessionId, connectionId);
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      setaccountDetails(response?.data);
    } else {
      navigate('/');
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sessionId, connectionId, navigate]);

  const deleteChannel = useCallback(async () => {
    dispatch(updateLoaderDetails(true));
    let response = await deleteAccount(connectionId, sessionId);
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      navigate('/');
      dispatch(updateBannerInfo(`${translate('language_service')} ${translate('successfully_deleted')}`, 'success'));
    } else if (response?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    } else {
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    }
  }, [dispatch, connectionId, sessionId, navigate, translate]);

  useEffect(() => {
    if (location.pathname === 'language-service/:id' && !beyondEightDotSeven) {
      navigate('/');
      return;
    }
    fetchAccountDetails();
  }, [fetchAccountDetails, location.pathname, beyondEightDotSeven, navigate]);

  const headerElement = () => {
    return (
      <>
        {!isApiKeyValid && (
          <Banner
            id="warning"
            variant="warning"
            heading=" "
            action={
              <Flex>
                <Link href={GoogleApiKeyGenLink} target="_blank" variant="link">
                  {translate('google_api_doc_link')}
                  <Icon name="open" />
                </Link>
              </Flex>
            }
            messages={[translate('invalid_google_api_key_warning')]}
          />
        )}
        {accountName && (
          <Text variant="h1" as="h1">
            {accountName}
          </Text>
        )}
      </>
    );
  };
  const toggleUpdate = () => setToggle(!toggle);
  const editUpdate = () => setEditFields(!editFields);
  const handleOnSubmit = () => {
    handleSubmit(updateConnectionData)();
  };

  const updateConnectionData = async (data: any) => {
    dispatch(updateLoaderDetails(true));
    const status = await validateApiKey(data.apiKey);
    const payload = {
      accountName: accountName,
      apiKey: data.apiKey,
      isApiKeyValid: status,
    };
    const json = await updateApiKey(sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      setaccountDetails(json?.data);
      dispatch(updateBannerInfo(translate('lang_config_update_success'), 'success'));
      setEditFields(false);
      setToggle(false);
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(`${translate('invalid_session')}`, 'urgent'));
    } else {
      let errorMessage;
      if (json?.data?.code) {
        errorMessage = translate(json.data.code);
      }
      dispatch(updateBannerInfo(errorMessage, 'urgent'));
    }
  };

  return (
    <SectionRenderTemplate header={headerElement()}>
      {accountName && (
        <Flex container={{ direction: 'column', itemGap: 1 }} item={{ grow: 1 }}>
          <Grid
            container={{
              cols: 'repeat(3, 1fr)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Label>{translate('configuration_type')}</Label>
            <Flex
              container={{
                direction: 'row',
                itemGap: 1,
                alignItems: 'center',
              }}
            >
              <Text variant="secondary">{translate('language_service')}</Text>
              <Image alt="Twitter" src={Logos.GoogleTranslateLogo} width="30px" height="40px" />
            </Flex>
          </Grid>

          <Grid
            container={{
              cols: 'repeat(2, 33% 65%)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Label>{translate('api_key')}</Label>

            <Flex
              container={{
                direction: 'row',
                alignItems: 'end',
                itemGap: 1,
              }}
            >
              {editFields || toggle ? (
                <Flex item={{ grow: 1 }}>
                  <TextArea
                    name="apiKey"
                    label="apiKey"
                    autoComplete="off"
                    type="text"
                    labelHidden={true}
                    required={true}
                    readOnly={!editFields}
                    style={{ minHeight: 100 }}
                    info={errors.apiKey?.message}
                    status={errors.apiKey?.message ? 'error' : undefined}
                    ref={register({
                      required: translate('api_key_error'),
                    })}
                  />
                </Flex>
              ) : (
                <Text variant="secondary">{getMaskedKey(apiKey, 35)}</Text>
              )}
              {!editFields && (
                <>
                  <Button style={tokenMaskerStyle} onClick={toggleUpdate}>
                    <Icon name={!toggle ? 'eye' : 'eye-off'} />
                  </Button>
                  <Button style={tokenMaskerStyle} onClick={editUpdate}>
                    <Icon name="pencil" />
                  </Button>
                </>
              )}
              {editFields && (
                <>
                  <Button onClick={handleOnSubmit} variant="primary" disabled={!formState.isValid}>
                    {translate('update')}
                  </Button>
                  <Button onClick={editUpdate} variant="secondary">
                    {translate('cancel')}
                  </Button>
                </>
              )}
            </Flex>
          </Grid>

          <Flex
            container={{
              direction: 'column',
              itemGap: 1,
            }}
          >
            <Flex style={{ paddingTop: '1.5rem', width: 'fit-content' }} ref={setShowTooltipEl}>
              <Button onClick={deleteConfirmation} disabled={isLanguageServiceInUse}>
                {translate('delete_connection_button')}
              </Button>
              {isLanguageServiceInUse && <Tooltip target={showTooltipEl}>{translate('disable_delete_info')}</Tooltip>}
            </Flex>
          </Flex>
        </Flex>
      )}
    </SectionRenderTemplate>
  );
};

export default LanguageService;
